import React from "react";
import { Input } from "../../../common/u1/Input";
import { ReadOnlyField } from "../../../common/u1/ReadOnlyField";
import { CountrySelect } from "../../../common/u1/CountrySelect";
import { CompanyMark } from "../../../common/u1/CompanyMark";
import { codeToFlagEmoji } from "../../../common/u1/countryFlag";

/* Step 1 — which company, and the representative confirmation. */
export const CompanyStep = ({
  country = "",
  countryCode = "",
  companyName = "",
  nameLocked = false,
  onCompanyNameChange = () => {},
  confirmed = false,
  onConfirmChange = () => {},
  errors = {},

  /* Search mode — omitted entirely when the company is already known. */
  searchable = false,
  onCountryChange,
  onLocateCountry,
  results = [],
  isSearching = false,
  selectedKey = null,
  onSelectCompany = () => {},
  notFound = false,
  onSwitchMode = () => {},
  minQuery = 3,
}) => {
  const showResults = searchable && !notFound && companyName.trim().length >= minQuery;

  return (
    <div className="grid gap-4">
      <div className="grid gap-3.5 sm:grid-cols-2">
        {searchable ? (
          <CountrySelect
            id="claim-country"
            label="Country"
            required
            value={countryCode}
            onChange={(c) => onCountryChange && onCountryChange(c.code)}
            onLocate={onLocateCountry}
            error={errors.country}
          />
        ) : (
          <ReadOnlyField
            label="Country"
            value={country}
            prefix={countryCode ? codeToFlagEmoji(countryCode) : null}
          />
        )}

        {nameLocked ? (
          <ReadOnlyField label="Company Name" value={companyName} />
        ) : (
          <Input
            id="claim-company-name"
            label="Company Name"
            labelVariant="caption"
            required
            value={companyName}
            onChange={(e) => onCompanyNameChange(e.target.value)}
            placeholder={
              searchable && !notFound
                ? "Start typing your registered business name"
                : "e.g. Al Azhar Al Hamra Trading LLC"
            }
            hint={
              searchable && !notFound
                ? `Type at least ${minQuery} characters to search the directory.`
                : "Use the legal name exactly as it appears on your trade license."
            }
            error={errors.companyName}
          />
        )}

        {/* Anchored under the field it belongs to, not spanning the card: the
            list describes what was typed here. In flow rather than an overlay
            so the step grows to fit it and nothing is clipped. */}
        {showResults && (
          <div
            className="col-start-auto -mt-1 max-h-[228px] overflow-y-auto pr-1 sm:col-start-2"
            role="radiogroup"
            aria-label="Matching businesses"
          >
            {isSearching && (
              <p role="status" className="py-6 text-center text-[13px] text-[var(--muted)] font-sans">
                Searching…
              </p>
            )}

            {!isSearching &&
              results.map((company) => {
                const key = company.companyID || company.wb_win || company.win || company.name;
                const label = company?.name || company?.registered_company_name || "";
                const checked = Boolean(selectedKey) && key === selectedKey;
                return (
                  <label
                    key={key}
                    /* `relative` so the sr-only radio is positioned against its own row. */
                    className={`relative mb-2 flex cursor-pointer items-center gap-3 rounded-[10px] border-[1.5px] p-3 transition-colors ${
                      checked
                        ? "border-[var(--orange)] bg-[var(--orange-soft)]"
                        : "border-[var(--field-border)] bg-white hover:border-[var(--field-border-hover)]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="business"
                      className="sr-only"
                      checked={checked}
                      onChange={() => onSelectCompany(company)}
                    />
                    <CompanyMark company={company} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold text-[var(--ink)] font-sans">
                        {label}
                      </span>
                      <span className="mt-0.5 block text-[12.5px] text-[var(--muted)] font-sans">
                        {[company?.city, company?.country || company?.country_code]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </span>
                  </label>
                );
              })}

            {!isSearching && results.length === 0 && (
              <p className="rounded-lg bg-[var(--slate-2)] px-3.5 py-3 text-[13px] text-[var(--muted)] font-sans">
                No businesses match that name yet.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Switches between picking from the directory and typing the name of a
          business TREVEX does not hold. Stays on this step either way. */}
      {searchable && (
        <p className="-mt-1 text-[13px] text-[var(--ink-2)] font-sans">
          {notFound ? (
            <>
              Want to search the directory instead?{" "}
              <button
                type="button"
                onClick={() => onSwitchMode(false)}
                className="font-semibold text-[var(--orange)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)]"
              >
                Go back to search
              </button>
            </>
          ) : (
            <>
              Cannot find the business?{" "}
              <button
                type="button"
                onClick={() => onSwitchMode(true)}
                className="font-semibold text-[var(--orange)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)]"
              >
                Click here
              </button>
            </>
          )}
        </p>
      )}

      {/* The whole block is the label, so the sentence is as clickable as the
          box — an 18px target alone is a poor one on a phone. */}
      <label
        className={`flex cursor-pointer items-start gap-3 rounded-[10px] border-[1.5px] p-4 transition-colors ${
          confirmed
            ? "border-[var(--orange)] bg-[var(--orange-soft)]"
            : "border-[var(--line)] bg-white hover:border-[var(--muted-2)]"
        }`}
      >
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => onConfirmChange(e.target.checked)}
          className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer accent-[var(--orange)]"
        />
        <span className="text-[13.5px] leading-[1.55] text-[var(--ink)] font-sans">
          I confirm that I am the <b>authorized representative</b> to manage our
          company&apos;s profile on TREVEX, allowing me to update and maintain our
          information as needed.
        </span>
      </label>
    </div>
  );
};

export default CompanyStep;
